/**************************************************************************************************/
/*   File: /src/app/shared/services/dossier/dossier.service.ts                                    */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Mon May 06 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { NGXLogger } from 'ngx-logger';
import { defer, from, Observable, of } from 'rxjs';
import { catchError, delay, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../../../environments/environment';
import * as fromEnums from '../../enums';
import * as fromModels from '../../models';
import { HeaderService } from '../utils/header.service';

/**
 * The dossier service
 */
@Injectable({
  providedIn: 'root'
})
export class DossierService {

  /**
  * The api url for authorization users
  */
  apiUrl = environment.serviceHost;
  /**
   * The api path
   */
  apiPath = environment.apiPath;
  /**
   * The dossiers url
   */
  path = 'portal-dossier';

  /**
   * The constructor
   * @param http The http 
   * @param logger the logger
   * @param headerService The header service
   */
  constructor(private http: HttpClient, private logger: NGXLogger, private headerService: HeaderService) {
  }

  /**
   * Request creation for dossier
   * @param {fromModels.DossierContent} dossierContent dossier content
   * @returns {Observable<fromModels.DossierId>} the dossier id
   */
  create(dossierContent: fromModels.DossierContent): Observable<fromModels.DossierId> {
    return this.http.post<fromModels.DossierId>(
      `${this.apiUrl}${this.apiPath}${this.path}/create`,
      dossierContent,
      this.headerService.getHeaders()
    );
  }
  /**
   * Request dossier deletion
   * @param {fromModels.DossierId} dossierId The dossier id
   * @returns {Observable<fromModels.Result>} the result
   */
  delete(dossierId: fromModels.DossierId): Observable<fromModels.Result> {
    return this.http.delete<fromModels.Result>(
      `${this.apiUrl}${this.apiPath}${this.path}/${dossierId.id}`
      , this.headerService.getHeaders());
  }
  /**
   * Request validation for dossier
   * @param {fromModels.DossierId} dossierId The dossier id
   * @returns {Observable<fromModels.Result>} the result
   */
  validate(dossierId: fromModels.DossierId): Observable<fromModels.Result> {
    return this.http.put<fromModels.Result>(
      `${this.apiUrl}${this.apiPath}${this.path}/validate/${dossierId.id}`,
      undefined,
      this.headerService.getHeaders());
  }
  /**
   * Send dossier
   * @param {fromModels.DossierId} dossierId The dossier id
   * @returns {Observable<fromModels.Result>} the result
   */
  delivery(dossierChangeStatus: fromModels.DossierChangeStatus): Observable<fromModels.Result> {
    return this.http.put<fromModels.Result>(
      `${this.apiUrl}${this.apiPath}${this.path}/delivery`,
      dossierChangeStatus,
      this.headerService.getHeaders());
  }
  /**
   * Check the status of the dossier with dossier id
   * @param {fromModels.DossierId} dossierId The dossier id
   * @returns {Observable<fromModels.DossierStatus>} the dossier status
   */
  checkstatus(dossierId: fromModels.DossierId): Observable<fromModels.DossierStatus> {
    return this.http.get<fromModels.DossierStatus>(
      `${this.apiUrl}${this.apiPath}${this.path}/status/${dossierId.id}`
      , this.headerService.getHeaders());
  }
  /**
   * Get the dossier by id
   * @param {fromModels.DossierId} dossierId The dossier id
   * @returns {Observable<fromModels.Dossier>} the dossier
   */
  getDossierById(dossierId: fromModels.DossierId): Observable<fromModels.Dossier> {
    return this.http.get<fromModels.Dossier>(
      `${this.apiUrl}${this.apiPath}${this.path}/dossier/${dossierId.id}`
      , this.headerService.getHeaders());
  }


  /**
  * Get the dossier by id
  * @returns {Observable<fromModels.ConsegnaSummary>} the dossier
  */
  getDossierDetailsById(idConsegna: fromModels.DossierId): Observable<fromModels.Dossier> {
    return this.http.get<fromModels.Dossier>(
      //'http://192.168.101.101:85/anagrafica/api/portal-dossier/public-dossier/'+idConsegna,
      `${this.apiUrl}${this.apiPath}${this.path}/public-dossier/`+idConsegna.id,
      this.headerService.getHeaders());
  }

  /**
  * Get  all the dossier
  * @return {Observable<Array<any>>}
  */
  getAllDossier(): Observable<fromModels.DossierSummary[]> {
    return this.http.get<fromModels.DossierSummary[]>(
      `${this.apiUrl}${this.apiPath}${this.path}/all`,
      this.headerService.getHeaders()
    );
  }
  /**
   * Create or update spatial data
   * @param {string} dossierId The dossier id
   * @param {fromEnums.SpatialDataTypeEnum} spatialDataType The spatial data type
   * @param {string} spatialDataId spatial data id
   */
  createOrUpdateSpatialData(
    dossierId: number,
    spatialDataType: fromEnums.SpatialDataTypeEnum,
    spatialDataId: string): Observable<fromModels.Result> {
    return this.http.put<fromModels.Result>(
      `${this.apiUrl}${this.apiPath}${this.path}/spatial-data`,
      {
        dossierId: dossierId,
        spatialDataType: spatialDataType,
        spatialData: {
          id: spatialDataId
        }
      },
      this.headerService.getHeaders()
    );
  }
  /**
   * Create or update other data
   * @param {string} dossierId The dossier id
   * @param {string} otherDocumentId spatial data id
   */
  createOrUpdateOtherData(dossierId: number, otherDocumentId: string): Observable<fromModels.Result> {
    return this.http.put<fromModels.Result>(
      `${this.apiUrl}${this.apiPath}${this.path}/other`,
      {
        dossierId: dossierId,
        documentId: {
          id: otherDocumentId
        }
      },
      this.headerService.getHeaders()
    );
  }
  /**
   * Create or update regulations data
   * @param {string} dossierId The dossier id
   * @param {string} territorialAreaCode territorialAreaCode
   */
  createOrUpdateRegulationsData(
    dossierId: number,
    territorialAreaCode: string,
    originalDoc: fromModels.LocalDocumentId,
    deltaDoc: fromModels.LocalDocumentId,
    finalDoc: fromModels.LocalDocumentId): Observable<fromModels.Result> {
    return this.http.put<fromModels.Result>(
      `${this.apiUrl}${this.apiPath}${this.path}/territorial-area-regulations`,
      {
        dossierId: dossierId,
        territorialAreaCode: territorialAreaCode,
        originalDoc: originalDoc,
        deltaDoc: deltaDoc,
        finalDoc: finalDoc
      },
      this.headerService.getHeaders()
    );
  }
}
